import type { AssetClientContext } from "@pej/asset-client";
import type { QueryClient } from "@tanstack/react-query";
import type { TemplateBody } from "@/api/templates";
import { pickArtworkUrl } from "./asset-projections";
import { manifestQuery } from "./asset-queries";

/**
 * Turns the artwork references a save is about to write into the public render
 * URLs stored beside them.
 *
 * This exists because the display page is public — capability token, no login —
 * so its endpoint has no session to read a delivery manifest with, while the
 * manifest endpoint is workspace-authenticated. The operator's session, here,
 * is the only one that can make the translation, so the admin makes it once at
 * save time and persists the answer. See migration 0004; when the backend gets
 * a credential of its own this becomes redundant and the server resolves again.
 *
 * Runs at submit rather than reading whatever the preview last rendered: the
 * preview's URL is a render-time value that can lag the form by a fetch, and
 * writing it would pair a newly picked asset with the previous one's artwork.
 * Everything here shares the manifest cache the preview and the picker already
 * use, so the common case costs no request at all.
 */
export interface ArtworkField {
    assetId: string | null;
    /** Whether this save changes which asset is referenced. */
    changed: boolean;
}

export type ArtworkUrls = Pick<TemplateBody, "backgroundImageUrl" | "logoUrl">;

/** A value to write, or nothing at all — "leave the stored column alone". */
type Resolution = { write: true; url: string | null } | { write: false };

export async function resolveArtworkUrls(
    ctx: AssetClientContext | null,
    queryClient: QueryClient,
    fields: { backgroundImage: ArtworkField; logo: ArtworkField },
): Promise<ArtworkUrls> {
    const [backgroundImage, logo] = await Promise.all([
        resolveOne(ctx, queryClient, fields.backgroundImage),
        resolveOne(ctx, queryClient, fields.logo),
    ]);
    return {
        ...(backgroundImage.write ? { backgroundImageUrl: backgroundImage.url } : {}),
        ...(logo.write ? { logoUrl: logo.url } : {}),
    };
}

async function resolveOne(
    ctx: AssetClientContext | null,
    queryClient: QueryClient,
    field: ArtworkField,
): Promise<Resolution> {
    // Nothing referenced. Clear the stored URL if this save is what removed
    // the reference; if the slot was simply never filled, say nothing at all
    // rather than putting a null in every body.
    if (!field.assetId) return field.changed ? { write: true, url: null } : { write: false };

    const url = ctx ? await manifestUrl(ctx, queryClient, field.assetId) : null;
    if (url) return { write: true, url };

    // Unresolvable — no asset service, a deleted asset, or the service being
    // briefly unreachable. What to do depends entirely on whether this save
    // is also moving the reference:
    //
    // - It is: the stored URL belongs to the *previous* asset, so keeping it
    //   would put the old artwork on screens under the new asset's id. Clear
    //   it and let the screen degrade to no artwork, as it does for every
    //   other asset failure in this product.
    // - It is not: the body is a patch, so omitting the key leaves the column
    //   untouched. A blip while someone edits the footer must not wipe artwork
    //   that is rendering correctly right now.
    return field.changed ? { write: true, url: null } : { write: false };
}

async function manifestUrl(ctx: AssetClientContext, queryClient: QueryClient, assetId: string) {
    try {
        return pickArtworkUrl(await queryClient.fetchQuery(manifestQuery(ctx, assetId)));
    } catch {
        // A failed resolution is never fatal to the save — the caller decides
        // what a missing URL means for this particular field.
        return null;
    }
}
